// import { useState } from 'react';
// import useAuth from '@hooks/useAuth/useAuth';
// // import { useNavigation } from '@react-navigation/native';
// import { setData } from '@utils/generalUtils/generalUtils';
// import { fetchSignup, fetchLogin, fetchPrivate } from '@api/auth/auth';
// import { Platform } from 'react-native';
// import { useAppNavigation } from '@context/NavigationContext/NavigationContext';

// export default function useLogin() {
// 	const { setAuth } = useAuth();
// 	// const navigation = useNavigation();

// 	const [email, setEmail] = useState('');
// 	const [password, setPassword] = useState('');
// 	const [confirmPassword, setConfirmPassword] = useState('');
// 	const [isLoading, setIsLoading] = useState(false);
// 	const [error, setError] = useState('');

// 	const { navigate } = useAppNavigation();

// 	const validateForm = type => {
// 		// Basic validation
// 		if (!email || !password) {
// 			setError('Please fill in all fields');
// 			return false;
// 		}
// 		if (type === 'signup' && (password !== confirmPassword || !confirmPassword)) {
// 			setError('Passwords do not match');
// 			return false;
// 		}
// 		// Additional validation as needed
// 		return true;
// 	};

// 	const onLoggedIn = async token => {
// 		console.log('Logged in!', token);
// 		if (await fetchPrivate(token)) {
// 			if (Platform.OS === 'android') {
// 				console.log('Navigating using android!');
// 				navigate('Home');
// 			} else {
// 				navigate('/home');
// 			}
// 		}
// 		return true;
// 	};

// 	const handleAuthentication = async (type, payload) => {
// 		if (!validateForm(type)) return;
// 		setIsLoading(true);
// 		setError('');
// 		try {
// 			let data;
// 			if (type === 'signup') {
// 				data = await fetchSignup(payload);
// 			} else {
// 				data = await fetchLogin(payload);
// 			}
// 			console.log(data);

// 			if (!data) {
// 				throw new Error(`Failed to ${type}`);
// 			}

// 			const valueArr = [data.name, data.role, data.email, data.id];

// 			let now = new Date();

// 			await setData('name', valueArr[0]);
// 			await setData('role', valueArr[1]);
// 			await setData('email', valueArr[2]);
// 			await setData('user_id', valueArr[3].toString());
// 			await setData('date_selected', now.toString());

// 			setAuth({
// 				id: data.id,
// 				name: data.name,
// 				email: data.email,
// 				role: data.role,
// 				token: data.token,
// 			});
// 			setIsLoading(false);
// 			return await onLoggedIn(data.token);
// 		} catch (err) {
// 			console.log(err);
// 			setError(err.message);
// 			setIsLoading(false);
// 		}
// 	};

// 	return {
// 		email,
// 		setEmail,
// 		password,
// 		setPassword,
// 		confirmPassword,
// 		setConfirmPassword,
// 		handleAuthentication,
// 		isLoading,
// 		setError,
// 		error,
// 	};
// }

// useLogin.js

import { useState } from 'react';
import useAuth from '@hooks/useAuth/useAuth';
import { setData } from '@utils/generalUtils/generalUtils';
import {
	fetchSignup,
	fetchLogin,
	fetchRefresh,
	fetchPrivate,
	fetchSetup2FA,
	fetchVerify2FA,
} from '@api/auth/auth'; // Ensure correct import path
import { Platform } from 'react-native';
import { useAppNavigation } from '@context/NavigationContext/NavigationContext';

export default function useLogin() {
	const { setAuth } = useAuth();
	const { navigate } = useAppNavigation();

	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');

	/**
	 * Validates the form inputs based on the authentication type.
	 * @param {string} type - The type of authentication ('login' or 'signup').
	 * @returns {boolean} - Returns true if validation passes, else false.
	 */
	const validateForm = type => {
		if (!email || !password) {
			setError('Please fill in all required fields.');
			return false;
		}

		if (type === 'signup') {
			if (!confirmPassword) {
				setError('Please confirm your password.');
				return false;
			}
			if (password !== confirmPassword) {
				setError('Passwords do not match.');
				return false;
			}
		}

		return true;
	};

	/**
	 * Handles actions after successful login or signup.
	 * @param {Object} response - The response object from the backend.
	 */
	const onLoggedIn = async response => {
		if (response.token) {
			// Optionally, store tokens or user data in AsyncStorage or SecureStore
			await setData('token', response.token);
			await setData('email', response.email);
			await setData('name', response.name);
			await setData('role', response.role);
			await setData('user_id', response.id.toString());

			setAuth({
				id: response.id,
				name: response.name,
				email: response.email,
				role: response.role,
				token: response.token,
			});

			// Navigate to the home screen
			if (Platform.OS === 'android') {
				navigate('Home');
			} else {
				navigate('/home');
			}
		} else {
			setError('Authentication failed.');
		}
	};

	/**
	 * Handles various authentication actions.
	 * @param {string} type - The type of authentication ('login', 'signup', 'setup-2fa', 'verify-2fa').
	 * @param {Object} payload - The data payload required for the authentication action.
	 * @returns {Object} - The response from the backend.
	 */
	const handleAuthentication = async (type, payload) => {
		if (!validateForm(type)) return;

		setIsLoading(true);
		setError('');

		try {
			let response;

			switch (type) {
				case 'signup':
					response = await fetchSignup(payload);
					break;
				case 'login':
					response = await fetchLogin(payload);
					break;
				case 'setup-2fa':
					response = await fetchSetup2FA(payload);
					break;
				case 'verify-2fa':
					response = await fetchVerify2FA(payload);
					break;
				default:
					throw new Error('Invalid authentication type.');
			}

			setIsLoading(false);

			if (type === 'signup') {
				if (response.two_fa_setup_required) {
					// 2FA setup required after signup
					return {
						two_fa_setup_required: true,
						qrCodeDataURL: response.qrCodeDataURL,
						userId: response.userId,
						tempToken: response.tempToken,
					};
				} else {
					// Regular signup without 2FA
					await onLoggedIn(response);
				}
			} else if (type === 'login') {
				if (response.two_fa_required) {
					// 2FA verification required after login
					return {
						two_fa_required: true,
						userId: response.userId,
						tempToken: response.tempToken,
					};
				} else if (response.two_fa_setup_required) {
					// 2FA setup required (e.g., after role promotion)
					return {
						two_fa_setup_required: true,
						qrCodeDataURL: response.qrCodeDataURL,
						userId: response.userId,
						tempToken: response.tempToken,
					};
				} else {
					// Regular login without 2FA
					await onLoggedIn(response);
				}
			} else if (type === 'setup-2fa' || type === 'verify-2fa') {
				// 2FA setup or verification completed
				await onLoggedIn(response);
			}

			return response;
		} catch (err) {
			setError(err.message || `Failed to ${type}.`);
			setIsLoading(false);
			return null;
		}
	};

	return {
		email,
		setEmail,
		password,
		setPassword,
		confirmPassword,
		setConfirmPassword,
		handleAuthentication,
		isLoading,
		setError,
		error,
	};
}
